import React, { useContext, useMemo } from 'react'
import { RouteProp, useRoute } from '@react-navigation/native'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { RootStackParamList } from '@src/navigation/types'
import { CheckoutContainer } from '@src/screens/Marketplace/components/checkout/CheckoutContainer'
import { MicrobiomeOutOfStockNotice } from '@src/screens/Marketplace/components/checkout/MicrobiomeOutOfStockNotice'
import { ProductDescription } from '@src/screens/Marketplace/components/checkout/ProductDescription'
import { ProductPayment } from '@src/screens/Marketplace/components/checkout/ProductPayment'
import {
  usePromotionCode,
  usePurchaseOneTimePayment,
  useTaxEstimation,
} from '@screens/Marketplace/utils/hooks'
import { BillingProduct, UiInlineAlert } from '@src/types'
import { InlineAlert } from '@src/components'
import { NavigatorContext } from '@utils'
import { useLazyScreenPreload } from '@navigation/utils.tsx'
import { userSelector } from '@src/selectors/app'
import { useEstimatedProductPrices } from '@src/screens/NutritionistHub/components/LabPanels/useEstimatedProductPrices'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { CheckoutLoadingContent } from './CheckoutLoadingContent'

export const DietitianMenuCheckout = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>()
  const route = useRoute<RouteProp<RootStackParamList, 'DietitianMenuCheckout'>>()
  const { enableLoader, disableLoader } = useContext(NavigatorContext)
  const user = useSelector(userSelector)
  const address = user?.address

  const { selection, goBackOnSuccess, nextScreen, hideDetails, showMicrobiomeTest } = route.params
  const { product, quantity = 1 } = selection
  const { title, description, shortDescription, details } = product
  const productDescription = description || shortDescription || ''

  useLazyScreenPreload('LabPanelsOrderConfirmed', {
    shouldPreload: nextScreen?.screen === 'LabPanelsOrderConfirmed',
  })

  const productKey = product.key as BillingProduct
  const promotionCode = usePromotionCode(productKey)

  const purchasableItems = useMemo(() => [{ product, quantity }], [product, quantity])
  const { estimatedProductPrices, loading } = useEstimatedProductPrices({ purchasableItems })
  const { estimatedTax } = useTaxEstimation({
    productKey,
    promotionCode,
    zipCode: address?.zipCode,
    country: address?.country,
  })

  const { purchaseOneTimePayment } = usePurchaseOneTimePayment()

  if (loading || estimatedProductPrices.length === 0) {
    return <CheckoutLoadingContent />
  }

  const estimate = estimatedProductPrices[0].estimatedPrice
  const priceDescription = `$${estimate.price}`

  const onSuccessNavigationAction = () => {
    if (nextScreen) {
      navigation.replace(nextScreen.screen, nextScreen.params)
      return
    }

    navigation.replace('DietitianAddonOrderConfirmed', { product, goBackOnSuccess })
  }

  const onCheckout = async () => {
    enableLoader()

    try {
      await purchaseOneTimePayment({
        product,
        quantity,
        promotionCode,
      })
      onSuccessNavigationAction()
    } finally {
      disableLoader()
    }
  }

  return (
    <CheckoutContainer
      checkoutButtonTitle="Purchase Now"
      checkoutDisabled={!selection}
      onCheckout={onCheckout}
    >
      <ProductDescription title={title} description={productDescription} price={priceDescription} />
      {showMicrobiomeTest && <MicrobiomeOutOfStockNotice />}
      <ProductPayment
        product={product.key as BillingProduct}
        fullPrice={estimate.price}
        estimatedTax={estimatedTax}
        promotionCode={promotionCode}
        additionalDiscount={estimate.discount}
      />
      {details && !hideDetails && (
        <InlineAlert
          category={UiInlineAlert.Info}
          message="HOW IT WORKS"
          secondaryMessage={details.join('\n')}
          style={styles.nextSteps}
        />
      )}
    </CheckoutContainer>
  )
}

const themedStyle = StyleService.create({
  groupProductContainer: {
    paddingHorizontal: 16,
    marginBottom: 16,
  },
  title: {
    marginTop: 24,
    marginBottom: 16,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  groupProductSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 40,
  },
  groupProductSelectorTitleContainer: {
    marginRight: 16,
    flexShrink: 1,
  },
  groupProductSelectorTitle: {
    color: 'theme.text.secondary',
  },
  arrowIcon: {
    width: 16,
    height: 16,
  },
  nextSteps: {
    marginHorizontal: 16,
    marginVertical: 32,
  },
  bottomSpacing: {
    marginBottom: 16,
  },
})
